import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SizableText, XStack } from 'tamagui';
import { useRouter } from 'expo-router';
import { Lesson } from '@/types';
import { Tooltip } from '@/components/ui/Tooltip';
import { useIsMobile } from '@/lib/useDeviceType';
import {
  lessonNavBottomSurface,
  lessonNavForeground,
} from '@/components/lesson/lessonNavButtonStyles';

interface LessonNavigationProps {
  previousLesson: Lesson | null;
  nextLesson: Lesson | null;
  courseSlug: string;
  onPreviousPress?: () => void;
  onNextPress?: () => void;
}

export function LessonNavigation({
  previousLesson,
  nextLesson,
  courseSlug,
  onPreviousPress,
  onNextPress,
}: LessonNavigationProps) {
  const router = useRouter();
  const isMobile = useIsMobile();

  const [prevHover, setPrevHover] = useState(false);
  const [prevPress, setPrevPress] = useState(false);
  const [nextHover, setNextHover] = useState(false);
  const [nextPress, setNextPress] = useState(false);

  const hasNext = !!nextLesson?.slug;
  const hasPrevious = !!previousLesson?.slug;

  // Mobile logic: 
  // - If has next: next expanded, previous icon only
  // - If no next: previous expanded, next icon only
  const isPreviousIconOnly = isMobile && hasNext;
  const isNextIconOnly = isMobile && !hasNext;
  const showPreviousText = hasPrevious && (!isMobile || !hasNext);
  const showNextText = hasNext && !isNextIconOnly;

  const prevElevated = hasPrevious && (prevHover || prevPress);
  const nextElevated = hasNext && (nextHover || nextPress);

  return (
    <XStack 
      justifyContent="space-between" 
      alignItems="center" 
      paddingVertical="$4"
      gap="$4"
      width="100%"
      marginTop="$2"
    >
      {/* Previous Button */}
      {hasPrevious ? (
        <Tooltip text={previousLesson.title || 'Previous Lesson'} placement="top">
          <Pressable
            onPress={() => {
              if (onPreviousPress) onPreviousPress();
              else router.replace(`/courses/${courseSlug}/${previousLesson.slug}`);
            }}
            onHoverIn={() => setPrevHover(true)}
            onHoverOut={() => setPrevHover(false)}
            onPressIn={() => setPrevPress(true)}
            onPressOut={() => setPrevPress(false)}
            style={lessonNavBottomSurface(true, prevElevated, isPreviousIconOnly, false)}
          >
            <Ionicons 
              name="chevron-back" 
              size={20} 
              color={lessonNavForeground(true, prevElevated)} 
            />
            {showPreviousText && (
              <SizableText 
                color={lessonNavForeground(true, prevElevated)}
                fontSize={16}
                size={16}
                fontWeight="600"
                lineHeight={20}
                letterSpacing={0.05}
                style={{ marginLeft: 8 }}
              >
                Previous Lesson
              </SizableText>
            )}
          </Pressable>
        </Tooltip>
      ) : (
        <Pressable
          disabled={true}
          style={lessonNavBottomSurface(false, false, isPreviousIconOnly, false)}
        >
          <Ionicons 
            name="chevron-back" 
            size={20} 
            color={lessonNavForeground(false, false)} 
          />
          {!isPreviousIconOnly && (
            <SizableText 
              color={lessonNavForeground(false, false)}
              fontSize={16}
              size={16}
              fontWeight="600"
              lineHeight={20}
              letterSpacing={0.05}
              style={{ marginLeft: 8 }}
            >
              Previous Lesson
            </SizableText>
          )}
        </Pressable>
      )}
      
      {/* Next Button */}
      {hasNext ? (
        <Tooltip text={nextLesson.title || 'Next Lesson'} placement="top">
          <Pressable
            onPress={() => {
              if (onNextPress) onNextPress();
              else router.replace(`/courses/${courseSlug}/${nextLesson.slug}`);
            }}
            onHoverIn={() => setNextHover(true)}
            onHoverOut={() => setNextHover(false)}
            onPressIn={() => setNextPress(true)}
            onPressOut={() => setNextPress(false)}
            style={lessonNavBottomSurface(true, nextElevated, isNextIconOnly, true)}
          >
            {showNextText && (
              <SizableText 
                color={lessonNavForeground(true, nextElevated)}
                fontSize={16}
                size={16}
                fontWeight="600"
                lineHeight={20}
                letterSpacing={0.05}
                style={{ marginRight: 8 }}
              >
                Next Lesson
              </SizableText>
            )}
            <Ionicons 
              name="chevron-forward" 
              size={20} 
              color={lessonNavForeground(true, nextElevated)} 
            />
          </Pressable>
        </Tooltip>
      ) : (
        <Pressable
          disabled={true}
          style={lessonNavBottomSurface(false, false, isNextIconOnly, true)}
        >
          {!isNextIconOnly && (
            <SizableText 
              color={lessonNavForeground(false, false)}
              fontSize={16}
              size={16}
              fontWeight="600"
              lineHeight={20}
              letterSpacing={0.05}
              style={{ marginRight: 8 }}
            >
              Next Lesson
            </SizableText>
          )}
          <Ionicons 
            name="chevron-forward" 
            size={20} 
            color={lessonNavForeground(false, false)} 
          />
        </Pressable>
      )}
    </XStack>
  );
}

